/**
 * Micro- en macro-animaties (README §4.6).
 *
 * - Reveal: blokken schuiven 16px omhoog en faden in zodra ze in beeld komen,
 *   met een kleine vertraging per broertje. Alleen als JS draait (klasse
 *   `smokkelaar-js` op <html>), dus zonder JS staat alles gewoon.
 * - Cart-badge: even opschalen bij elke winkelwagenwijziging.
 * - Header: schaduw zodra er gescrold is (Horizon scrolt in .page-wrapper).
 *
 * Respecteert prefers-reduced-motion: dan gebeurt er niets.
 */

const REVEAL = [
  '.smokkelaar-stijl__tegel',
  '.smokkelaar-stijl',
  '.smokkelaar-service__tegel',
  '.smokkelaar-faqgroep',
  '.smokkelaar-cadeau',
  '.smokkelaar-cadeaus__onder > *',
  '.smokkelaar-proeflokaal__tegel',
  '.smokkelaar-proeflokaal__verhaal',
  '.smokkelaar-proeflokaal__proeverij',
  '.smokkelaar-brouwerijen__kaart',
  '.smokkelaar-brouwerij__feiten',
  '.smokkelaar-verlanglijst__kaart',
  '.smokkelaar-sixpack-cadeaus__kaart',
  '.smokkelaar-usp',
  '.smokkelaar-tipkaart',
  '.smokkelaar-verhaal',
  '.smokkelaar-contact__kaart',
  'main .shopify-section:not(:first-child) .section > .group-block, main .shopify-section:not(:first-child) .section-background + .section > *',
].join(', ');

const rustig = matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!rustig) {
  // De klasse zet de reveal-CSS aan. Alleen op desktop: op een telefoon is bijna elk
  // blok hoger dan het scherm en bleef inhoud te lang onzichtbaar (live audit, nr. 16).
  if (matchMedia('(min-width: 750px)').matches) document.documentElement.classList.add('smokkelaar-js');

  // Reveal
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );
  const observeer = (root = document) => {
    root.querySelectorAll(REVEAL).forEach((el) => {
      if (el.dataset.reveal) return;
      el.dataset.reveal = '1';
      // Vertraging per positie onder dezelfde ouder, maximaal 6 stappen.
      const i = Math.min([...el.parentElement.children].indexOf(el), 6);
      el.style.setProperty('--smokkelaar-i', String(i));
      io.observe(el);
    });
  };
  observeer();
  // Blokken die later verschijnen (quiz, verlanglijst, sectie-herrenders).
  document.addEventListener('smokkelaar:verlanglijst', () => setTimeout(() => observeer(), 50));

  // Cart-badge pop bij elke wijziging
  const pop = () =>
    setTimeout(() => {
      document.querySelectorAll('.cart-bubble').forEach((b) => {
        b.classList.remove('is-pop');
        void b.offsetWidth;
        b.classList.add('is-pop');
      });
    }, 120);
  document.addEventListener('shopify:cart:lines-update', pop);

  // Header-schaduw na scrollen
  // Horizon scrolt pas vanaf 990 px in .page-wrapper; daaronder scrolt het venster.
  const wrapper = (matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) || window;
  const header = document.querySelector('header-component') || document.querySelector('.header');
  if (header) {
    let laatst = null;
    wrapper.addEventListener(
      'scroll',
      () => {
        const y = wrapper === window ? window.scrollY : wrapper.scrollTop;
        const aan = y > 8;
        if (aan !== laatst) {
          header.classList.toggle('is-scrolled', aan);
          laatst = aan;
        }
      },
      { passive: true }
    );
  }
}

/**
 * Aankondiging per dagdeel (item 17). De server raadt met Liquid 'now', maar die
 * HTML komt uit de CDN-cache en kan verlopen zijn; hier zetten we de tekst goed
 * op de klok van Oldenzaal, waar de bezoeker ook zit. Vrijdag na de besteltijd
 * telt als weekend.
 */
const zetDagdeel = () => {
  const el = document.querySelector('.smokkelaar-dagdeel');
  if (!el) return;
  const delen = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    weekday: 'short',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const lees = (type) => delen.find((d) => d.type === type)?.value ?? '';
  const dag = lees('weekday'); // Mon … Sun
  const uur = Number(lees('hour'));
  const grens = Number(el.dataset.grens) || 16;
  const weekend = dag === 'Sat' || dag === 'Sun' || (dag === 'Fri' && uur >= grens);
  let tekst = weekend ? el.dataset.weekend : uur < grens ? el.dataset.voor : el.dataset.na;
  // Op een telefoon past alleen het eerste deel op één regel; "Gratis afhalen" staat ook in de footer en de lade.
  if (tekst && matchMedia('(max-width: 749px)').matches) tekst = tekst.split(' · ')[0];
  if (tekst && el.textContent.trim() !== tekst) el.textContent = tekst;
};
zetDagdeel();
// In de theme editor rendert de sectie opnieuw via Liquid; dan nog eens kiezen.
document.addEventListener('shopify:section:load', zetDagdeel);

/**
 * Externe links (Bierkluis.nl ↗, ook de kaart in de hero) openen in een nieuw
 * tabblad: de navigatie in de admin kent geen target, en de klant hoort de
 * winkel niet kwijt te raken.
 */
const zetExterneLinks = (root = document) =>
  root.querySelectorAll('a[href^="https://bierkluis.nl"]').forEach((a) => {
    a.target = '_blank';
    a.rel = 'noopener';
  });
zetExterneLinks();
document.addEventListener('shopify:section:load', (e) => zetExterneLinks(e.target));
